import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ContadorMinimo from "../ContadorMinimo";
import Formulario from "../Formulario";
import closeIcon from "../../../assets/icons/close.svg";
import SolicitacaoPatrocinador, { MAX_MENSAGEM, MIN_MENSAGEM } from "../../../models/SolicitacaoPatrocinador";
import { linkValido, MENSAGEM_LINK, normalizarLink } from "../../../utils/links";
import { mensagemDeErro, respostaOk } from "../../../utils/resposta";
import { CardPatrocinio, Overlay } from "./styled";

const MAX_EMPRESA = 100;
const MAX_LINK = 100;

/**
 * Pedido para ser PATROCINADOR (o "Quero ser um patrocinador" do cartão "Sua empresa
 * aqui", no Fórum e no Perfil).
 *
 * Antes o botão abria um link de formulário externo que o painel não edita mais. Agora
 * é como o pedido de perfil Profissional: a pessoa preenche aqui, o pedido chega ao
 * painel do administrador, que aprova ou recusa e responde pelo e-mail/celular do
 * perfil.
 *
 * Estados, decididos por GET /solicitacoes-patrocinador/minha:
 *   formulario   nunca pediu, ou o último pedido já foi decidido (aprovado ou recusado):
 *                mostra a resposta da administração no topo e deixa pedir de novo
 *   pendente     há um pedido em análise (mostra o que foi enviado)
 *   erro         não foi possível consultar
 */
export default function ModalSolicitarPatrocinio({ onClose }) {
  const [etapa, setEtapa] = useState("carregando");
  const [solicitacao, setSolicitacao] = useState(null);
  const [contato, setContato] = useState(null);
  const [form, setForm] = useState({ ...SolicitacaoPatrocinador.model });
  const [errors, setErrors] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [recemEnviado, setRecemEnviado] = useState(false);
  const campoInicial = useRef(null);

  async function carregar() {
    setEtapa("carregando");
    const resultado = await SolicitacaoPatrocinador.minha();

    if (!respostaOk(resultado)) {
      setErrors({ geral: mensagemDeErro(resultado) });
      setEtapa("erro");
      return;
    }

    setSolicitacao(resultado.data.solicitacao);
    setContato(resultado.data.contato);
    setErrors({});
    setEtapa(resultado.data.solicitacao?.status === "Pendente" ? "pendente" : "formulario");
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc fecha, como nos outros diálogos do painel.
  useEffect(() => {
    const aoTeclar = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [onClose]);

  useEffect(() => {
    if (etapa === "formulario") campoInicial.current?.focus();
  }, [etapa]);

  function alterar(campo, valor) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setErrors((atual) => ({ ...atual, [campo]: "", geral: "" }));
  }

  // Valida tudo de uma vez: a pessoa vê o que falta sem corrigir um campo por tentativa.
  function validar() {
    const novos = {};
    if (form.empresa.trim().length < 2) novos.empresa = "Informe o nome da empresa ou marca.";

    const link = normalizarLink(form.link);
    if (link) {
      if (!linkValido(link)) novos.link = MENSAGEM_LINK;
      else if (link.length > MAX_LINK) novos.link = `O link pode ter no máximo ${MAX_LINK} caracteres.`;
    }

    if (form.mensagem.trim().length < MIN_MENSAGEM) {
      novos.mensagem = `Conte um pouco mais: mínimo de ${MIN_MENSAGEM} caracteres.`;
    }
    return { novos, link };
  }

  async function enviar(e) {
    e.preventDefault();
    const { novos, link } = validar();
    setErrors(novos);
    if (Object.keys(novos).length) {
      // Foca o primeiro campo com erro, na ordem do formulário.
      const primeiro = ["empresa", "link", "mensagem"].find((c) => novos[c]);
      document.getElementById(`patrocinio-${primeiro}`)?.focus();
      return;
    }

    setEnviando(true);
    const resultado = await SolicitacaoPatrocinador.create({
      empresa: form.empresa.trim(),
      ...(link ? { link } : {}),
      mensagem: form.mensagem.trim(),
    });

    if (respostaOk(resultado)) {
      setForm({ ...SolicitacaoPatrocinador.model });
      setRecemEnviado(true);
      await carregar();
    } else {
      setErrors({ geral: mensagemDeErro(resultado) });
      // 409: já existe pedido pendente — a tela estava desatualizada. Recarrega para
      // mostrar o estado verdadeiro.
      if (resultado?.response?.status === 409) await carregar();
    }
    setEnviando(false);
  }

  const celular = contato?.celular || "celular não informado";

  return (
    <Overlay>
      <CardPatrocinio role="dialog" aria-modal="true" aria-labelledby="titulo-patrocinio">
        <div className="top">
          <h3 id="titulo-patrocinio">Quero ser um patrocinador</h3>
        </div>
        <button type="button" className="close" onClick={onClose} aria-label="Fechar">
          <img src={closeIcon} alt="" />
        </button>

        {etapa === "carregando" &&
          <p className="carregando" role="status">Verificando o seu pedido...</p>
        }

        {etapa === "erro" &&
          <>
            <p className="geral" role="alert">{errors.geral}</p>
            <div className="acoes">
              <button type="button" onClick={carregar}>Tentar novamente</button>
              <button type="button" className="secundario" onClick={onClose}>Fechar</button>
            </div>
          </>
        }

        {etapa === "pendente" &&
          <>
            <div className="estado" role="status">
              <strong>{recemEnviado ? "Recebemos o seu pedido. Obrigado!" : "Seu pedido está em análise"}</strong>
              Enviado em {new Date(solicitacao?.createdAt).toLocaleDateString("pt-BR")}. A administração vai avaliar e
              responder para {contato?.email}{contato?.celular ? ` ou ${contato.celular}` : ""}.
            </div>
            <ul className="enviado">
              <li><b>Empresa ou marca:</b> {solicitacao?.empresa}</li>
              <li><b>Site:</b> {solicitacao?.link || "não informado"}</li>
              <li><b>Mensagem:</b> {solicitacao?.mensagem}</li>
            </ul>
            <div className="acoes">
              <button type="button" onClick={onClose}>Entendi</button>
            </div>
          </>
        }

        {etapa === "formulario" &&
          <>
            {solicitacao?.status === "Recusada" &&
              <div className="estado recusada">
                <strong>Seu último pedido não foi aprovado</strong>
                {solicitacao.resposta
                  ? <>Resposta da administração: “{solicitacao.resposta}”.</>
                  : <>A administração não informou um motivo.</>}
                {" "}Você pode enviar um novo pedido com mais informações.
              </div>
            }
            {solicitacao?.status === "Aprovada" &&
              <div className="estado">
                <strong>Seu último pedido foi aprovado</strong>
                {solicitacao.resposta
                  ? <>Mensagem da administração: “{solicitacao.resposta}”.</>
                  : <>A administração vai entrar em contato com você.</>}
                {" "}Se quiser patrocinar com outra empresa ou marca, envie um novo pedido.
              </div>
            }

            <p className="intro">
              Patrocinadores aparecem como banner no app e no site, para todos os plantadores do Brasil. Conte sobre a
              sua empresa: a administração analisa o pedido e entra em contato.
            </p>

            <Formulario id="form-solicitar-patrocinio">
              <div className="form-group">
                <label htmlFor="patrocinio-empresa">Empresa ou marca</label>
                <input id="patrocinio-empresa" name="empresa" type="text" ref={campoInicial} maxLength={MAX_EMPRESA}
                  value={form.empresa} placeholder="Nome que vai aparecer no banner"
                  onChange={(e) => alterar("empresa", e.target.value)} className={errors.empresa && "input-error"}
                  aria-invalid={!!errors.empresa} />
                <span className="error" role="alert">{errors.empresa}</span>
              </div>

              <div className="form-group">
                <label htmlFor="patrocinio-link">Site (opcional)</label>
                <input id="patrocinio-link" name="link" type="text" inputMode="url" autoComplete="url"
                  value={form.link} placeholder="www.suaempresa.com.br"
                  onChange={(e) => alterar("link", e.target.value)} className={errors.link && "input-error"}
                  aria-invalid={!!errors.link} />
                <span className="error" role="alert">{errors.link}</span>
              </div>

              <div className="form-group">
                <label htmlFor="patrocinio-mensagem">Conte como pretende patrocinar</label>
                <textarea id="patrocinio-mensagem" name="mensagem" maxLength={MAX_MENSAGEM} value={form.mensagem}
                  placeholder="Ex.: queremos divulgar a nossa marca no app e apoiar o plantio de árvores."
                  onChange={(e) => alterar("mensagem", e.target.value)} className={errors.mensagem && "input-error"}
                  aria-invalid={!!errors.mensagem} />
                <span className="contador">{form.mensagem.length}/{MAX_MENSAGEM}</span>
                <ContadorMinimo valor={form.mensagem} minimo={MIN_MENSAGEM} campo="A mensagem" />
              </div>

              <p className="contato">
                <strong>Vamos responder para:</strong> {contato?.email} · {celular}.<br />
                Confira no seu <Link to="/perfil" onClick={onClose}>Perfil</Link>; é por aí que a administração fala com você.
              </p>

              <p className="geral" role="alert">{errors.geral}</p>

              <div className="acoes">
                <button type="submit" onClick={enviar} disabled={enviando}>
                  {enviando ? "Enviando..." : "Enviar pedido"}
                </button>
                <button type="button" className="secundario" onClick={onClose}>Agora não</button>
              </div>
            </Formulario>
          </>
        }
      </CardPatrocinio>
    </Overlay>
  );
}
